import { ROUTES, RouteName } from '@/constants';
import {
  DIRECTORY_PAGE_SIZE,
  type DirectoryType,
  type IDirectoryItem,
  getDirectoryPage,
  isSupportedDirectoryLocale,
} from '@/fetchers/contentfulAPI/directory.fetchers';
import { checkIsPreviewModeRequest, parsePageParam, sanitizeGraphQLString, setPublicPageCacheHeader } from '@/utils';
import type { GetServerSideProps } from 'next';

/**
 * Copy of /carrier and /region, hardcoded in English on purpose: the directories launch
 * English-only and have no translation entries yet, and `t()` renders an unknown key as the key
 * itself ("all_carriers_title"). Move to MasterTranslation once the keys exist.
 */
export const DIRECTORY_COPY: Record<DirectoryType, { title: string; description: string; searchPlaceholder: string }> =
  {
    carriers: {
      title: 'All Carriers',
      description:
        'Browse every parcel carrier supported by Parcel Monitor and open a carrier page to track your shipment in one place.',
      searchPlaceholder: 'Search carriers',
    },
    regions: {
      title: 'Tracking Around the World',
      description:
        "Browse parcel tracking by country and region. Open a page, drop in your tracking number, and follow your order wherever in the world it's heading.",
      searchPlaceholder: 'Search countries or regions',
    },
  };

/**
 * English display name of every locale in SUPPORTED_LOCALES, for the directory language filter.
 * Deliberately NOT read from LANGUAGES: that list is keyed by the PMT LanguageCode
 * (`zh-cn`, `zh-tw`, `nb`, `sr`, …), which is a different set from the URL locales — `zh` has no
 * entry at all, so the filter would show the raw code ("ZH") — and a few of its englishName values
 * are country names or padded ("Romania", "Thai language").
 * Keep in sync with SUPPORTED_LOCALES; the order there is already alphabetical by these names.
 */
export const LOCALE_DISPLAY_NAMES: Record<string, string> = {
  zh: 'Chinese',
  nl: 'Dutch',
  en: 'English',
  fr: 'French',
  de: 'German',
  el: 'Greek',
  hi: 'Hindi',
  hu: 'Hungarian',
  id: 'Indonesian',
  it: 'Italian',
  ja: 'Japanese',
  ko: 'Korean',
  ms: 'Malay',
  pl: 'Polish',
  pt: 'Portuguese',
  ro: 'Romanian',
  ru: 'Russian',
  es: 'Spanish',
  ta: 'Tamil',
  th: 'Thai',
  tr: 'Turkish',
  vi: 'Vietnamese',
};

export const DIRECTORY_SHARED_COPY = {
  allLocales: 'All languages',
  localeFilterLabel: 'Filter by language',
  noResults: 'No results found',
};

/**
 * A one-character term matches most of the list, so it costs a full-table Contentful query to
 * tell the user nothing. The check has to live server-side as well as in the input: ?q= is part
 * of the URL now, so anyone — or any crawler walking query strings — can request one directly.
 */
export const DIRECTORY_SEARCH_MIN_LENGTH = 2;

/**
 * `?q=` reaches Contentful, so it is validated the same way as any other query param.
 * Sanitizing before the length check makes the parsed term byte-identical to what the GraphQL
 * filter receives, so the input box, the canonical URL and the query can never disagree.
 * A term that is too short is ignored rather than a 404 — a stray ?q=a should still show the hub.
 */
export const parseDirectorySearchParam = (rawSearchTerm: string | string[] | undefined): string | undefined => {
  if (typeof rawSearchTerm !== 'string') return undefined;

  const searchTerm = sanitizeGraphQLString(rawSearchTerm);

  return searchTerm.length >= DIRECTORY_SEARCH_MIN_LENGTH ? searchTerm : undefined;
};

/**
 * Directory slugs are "<locale>/track-<name>", so the locale a page is written in is
 * already in the slug — no extra Contentful field needed for the card's language badge.
 * Returns undefined for anything that isn't a supported locale prefix.
 */
export const getSlugLocale = (slug: string): string | undefined => {
  const [prefix] = slug.split('/');

  return isSupportedDirectoryLocale(prefix) ? prefix : undefined;
};

export const getLocaleDisplayName = (locale: string) => LOCALE_DISPLAY_NAMES[locale] ?? locale.toUpperCase();

const DIRECTORY_ITEM_NOUN: Record<DirectoryType, { one: string; many: string }> = {
  carriers: { one: 'carrier', many: 'carriers' },
  regions: { one: 'destination', many: 'destinations' },
};

/**
 * "1,321 carriers" while browsing, "205 matching carriers" while searching. Both are the full
 * count, not the slice on screen: a search is paginated like any other view, so every match the
 * number claims is reachable.
 */
export const formatDirectoryCount = ({
  type,
  total,
  isSearching,
}: { type: DirectoryType; total: number; isSearching?: boolean }) => {
  const noun = total === 1 ? DIRECTORY_ITEM_NOUN[type].one : DIRECTORY_ITEM_NOUN[type].many;
  const formattedTotal = total.toLocaleString('en-US');

  return isSearching ? `${formattedTotal} matching ${noun}` : `${formattedTotal} ${noun}`;
};

export interface IDirectoryPageServerSideProps {
  type: DirectoryType;
  items: IDirectoryItem[];
  total: number;
  currentPage: number;
  totalPages: number;
  locale: string | null;
  searchTerm: string | null;
}

/**
 * Shared getServerSideProps for the /carrier and /region directories.
 * Directly queries Contentful — no Redis in v1, and the existing Redis thunk would
 * collide anyway: it keys on resolvedUrl without the query string, so every ?page=N
 * would share one cache entry.
 *
 * Search runs here rather than in the browser so that browsing and searching are one code
 * path: `?q=` is just an extra `where` clause, which is what lets a search be paginated at
 * all — `total` is then the number of matches, not the size of the whole list.
 */
export const createDirectoryGetServerSideProps = (
  type: DirectoryType,
): GetServerSideProps<IDirectoryPageServerSideProps> => {
  return async ({ query, res }) => {
    const page = parsePageParam(query.page);
    if (page === null) return { notFound: true };

    const rawLocale = Array.isArray(query.locale) ? undefined : query.locale;
    if (rawLocale && !isSupportedDirectoryLocale(rawLocale)) return { notFound: true };
    const locale = rawLocale ?? null;

    const searchTerm = parseDirectorySearchParam(query.q);

    const isPreviewMode = checkIsPreviewModeRequest(query);
    const result = await getDirectoryPage({ type, page, locale: locale ?? undefined, searchTerm, isPreviewMode });

    // A Contentful outage must surface as a 5xx, never a cacheable empty page or 404.
    if (!result) {
      throw new Error(`getDirectoryPage(${type}) returned no data`);
    }

    const totalPages = Math.max(1, Math.ceil(result.total / DIRECTORY_PAGE_SIZE));
    if (page > totalPages) return { notFound: true };

    // Browsing has a bounded set of URLs (pages × locales) that a shared cache can hold;
    // ?q= does not, so it must not advertise 12h of shared caching — one entry per term
    // anybody types.
    if (!searchTerm) {
      setPublicPageCacheHeader(res, isPreviewMode);
    }

    return {
      props: {
        type,
        items: result.items,
        total: result.total,
        currentPage: page,
        totalPages,
        locale,
        searchTerm: searchTerm ?? null,
      },
    };
  };
};

/**
 * The directory keys are plural ("carriers") but the URLs are singular ("/carrier"), so the
 * path has to come from ROUTES rather than the key — that keeps ROUTES the one place a
 * directory can be renamed.
 */
const DIRECTORY_ROUTE_NAME: Record<DirectoryType, RouteName> = {
  carriers: RouteName.Carriers,
  regions: RouteName.Regions,
};

export const getDirectoryHubPath = (type: DirectoryType) => ROUTES[DIRECTORY_ROUTE_NAME[type]].path;

/**
 * The URL a directory view is addressed by — and, via `publicRuntimeConfig.origin`, the canonical
 * both /carrier and /region emit. The indexing rules those two pages share:
 *
 * Every paginated URL points its canonical at itself. Canonical-to-page-1 would stop Google
 * crawling pages 2+ and waste the internal links they carry. Filtered views are self-referencing
 * too: pairing noindex with a canonical pointing elsewhere sends two contradictory instructions.
 *
 * ?page=N stays indexable — those are the pages that carry the links to every entry. ?locale=X
 * does not: 22 near-identical subsets of one list is exactly the thin content the site is being
 * penalised for. ?q= is the same problem without a ceiling, one thin near-duplicate list per term
 * a crawler cares to invent. Both are noindex,follow — `follow` keeps their links crawlable, and a
 * canonical would not do instead: it is a hint Google may ignore and never stops the URL being
 * crawled.
 */
export const buildDirectoryPath = (
  type: DirectoryType,
  { page, locale, searchTerm }: { page?: number; locale?: string | null; searchTerm?: string | null },
) => {
  const params = new URLSearchParams();
  if (locale) params.set('locale', locale);
  if (searchTerm) params.set('q', searchTerm);
  if (page && page > 1) params.set('page', String(page));
  const queryString = params.toString();

  return `${getDirectoryHubPath(type)}${queryString ? `?${queryString}` : ''}`;
};
